/**
 * FilterBar.jsx — reusable filter row for list pages.
 *
 * Spec-driven: pass in a `search` handle + an array of `filters`, each
 * describing one dropdown. Bar auto-renders, auto-counts active filters, and
 * exposes a single "Clear · N" pill to reset everything.
 *
 * Usage:
 *   <FilterBar
 *     search={{ value: q, onChange: setQ, placeholder: 'Search order number…' }}
 *     filters={[
 *       { key: 'status', value: status, onChange: setStatus, options: [{value:'', label:'All'}, ...] },
 *       { key: 'supplier', value: supplier, onChange: setSupplier, options: [...] },
 *     ]}
 *     onClear={() => { setQ(''); setStatus(''); setSupplier(''); }}
 *   />
 *
 * Each `options` item is { value, label }. An empty-string `value` is treated
 * as the "unfiltered" choice for active-filter counting.
 */
import React from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';

export default function FilterBar({ search, filters = [], onClear, className = '', testId = 'filter-bar' }) {
  const activeCount = (search?.value ? 1 : 0) + filters.filter((f) => f.value).length;

  return (
    <div
      className={`bg-card border border-border rounded-lg p-3 mb-4 ${className}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {search && (
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              value={search.value || ''}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder || 'Search…'}
              data-testid={`${testId}-search`}
              aria-label={search.placeholder || 'Search'}
              className="w-full h-10 pl-9 pr-3 text-sm bg-background border border-border rounded focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            />
          </div>
        )}
        {filters.map((f) => (
          <select
            key={f.key}
            value={f.value || ''}
            onChange={(e) => f.onChange(e.target.value)}
            data-testid={`${testId}-${f.key}`}
            aria-label={f.label || f.key}
            className={`h-10 px-3 text-sm bg-background border border-border rounded ${f.className || ''}`}
          >
            {(f.options || []).map((o) => (
              <option key={o.value || '__empty__'} value={o.value}>{o.label}</option>
            ))}
          </select>
        ))}
        {activeCount > 0 && onClear && (
          <button
            type="button"
            onClick={onClear}
            data-testid={`${testId}-clear`}
            className="h-10 px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground border border-border rounded hover:bg-muted inline-flex items-center gap-1.5"
          >
            <X size={12} weight="bold" /> Clear · {activeCount}
          </button>
        )}
      </div>
    </div>
  );
}
